import { PriceGroup } from "@/types/pricingVersion";
import {
    applyDiscountToCents,
    formatMoneyWithCurrency,
} from "@/utils/moneyFormatting";
import { ReactNode } from "react";
import { FaCheckCircle } from "react-icons/fa";
import { FaXmark } from "react-icons/fa6";
import { GoDash } from "react-icons/go";
import { RiInformationFill } from "react-icons/ri";
import { PaymentFormData } from "../Onboarding/Client/PaymentForm/PaymentForm";
import Tooltip from "../Tooltip";

const check = <FaCheckCircle className="text-xl" />;
const x = <FaXmark className="text-2xl" />;
const varies = (text: string) => (
    <Tooltip title={text}>
        <span className="inline-flex items-center">
            Varies
            <span className="relative ml-1 inline-flex grow">
                <RiInformationFill
                    size={14}
                    className="items-center justify-center text-brand-blueDecal md:text-lg"
                />
            </span>
        </span>
    </Tooltip>
);
const dash = <GoDash className="text-2xl" />;

export default function ComparisonChart({
    paymentFormData,
}: {
    paymentFormData: PaymentFormData;
}) {
    const centsPerPriceGroup =
        paymentFormData.pricingVersion.trialCostAmountCentsPerPriceGroup;
    const displayPerPriceGroup =
        paymentFormData.pricingVersion.trialCostAmountPerPriceGroupForDisplay;

    let groupKeyForDisplay: PriceGroup | null = null;
    if (centsPerPriceGroup) {
        groupKeyForDisplay = "default";
    }

    let baseCents = paymentFormData.pricingVersion.trialCostAmountCents ?? 0;
    if (centsPerPriceGroup && groupKeyForDisplay) {
        baseCents = centsPerPriceGroup[groupKeyForDisplay] ?? 0;
    }
    let baseCostForDisplay =
        paymentFormData.pricingVersion.trialCostAmountForDisplay ?? "0";
    if (displayPerPriceGroup && groupKeyForDisplay) {
        baseCostForDisplay = displayPerPriceGroup[groupKeyForDisplay] ?? "0";
    }
    const costAfterPromoCents = applyDiscountToCents(
        baseCents,
        paymentFormData.promotionCode?.coupon,
    );
    const costAfterPromoForDisplay = formatMoneyWithCurrency(
        costAfterPromoCents,
        paymentFormData.prices[0].currency ?? "usd",
    );

    const chartData = [
        [
            "",
            <img
                src={`${import.meta.env.VITE_CDN_URL}/images/favicon.webp`}
                alt="logo"
                className="w-full max-w-12 p-1"
            />,
            <span className="text-sm font-bold text-brand-black">
                Coach Private Practice
            </span>,
        ],
        ["Personalized coach match", check, x],
        ["1:1 video sessions", check, check],
        [
            "Message your coach anytime",
            check,
            varies(
                "Most coaches don't provide in-between session communication to clients",
            ),
        ],
        [
            "Exercises between sessions",
            check,
            varies(
                "Where most coaches rely on a limited set of go-to exercises, Strawberry.me coaches pull from an extensive library built around your unique needs.",
            ),
        ],
        [
            "Cancel anytime",
            check,
            varies(
                "Many conventional coaching programs require upfront purchase of many sessions at once. With Strawberry.me you can cancel your subscription anytime.",
            ),
        ],
        ["Upfront commitment required", x, check],
        [
            "Cost",
            <div>{baseCostForDisplay}/wk</div>,
            paymentFormData.prices[0].currency === "cad"
                ? "$150 - $300 CAD/session"
                : "$150 - $300/session",
        ],
        paymentFormData.promotionCode
            ? [
                  "Trial price",
                  <span>
                      <span className="line-through">{baseCostForDisplay}</span>{" "}
                      <span>{costAfterPromoForDisplay}</span>
                      /wk
                  </span>,
                  dash,
              ]
            : null,
    ] as const satisfies (null | [ReactNode, ReactNode, ReactNode])[];

    return (
        <div className="grid grid-cols-[auto_1fr_1fr] gap-x-3 border -mx-4 sm:-mx-0 mt-4 border-brand-border overflow-hidden rounded-lg [&>*:first-child]:!bg-brand-lightGray [&>*:nth-child(2n+1)]:bg-brand-lightGray/60">
            {chartData
                .filter((row) => row !== null)
                .map(([title, us, them]) => (
                    <div
                        key={title}
                        className="grid grid-cols-subgrid col-span-full items-center justify-items-center text-center px-2 sm:px-4 py-2"
                    >
                        <div className="font-medium text-brand-black py-2 justify-self-start text-left">
                            {title}
                        </div>
                        <div className="text-sm font-medium text-brand-red">
                            {us}
                        </div>
                        <div className="text-sm font-medium">{them}</div>
                    </div>
                ))}
        </div>
    );
}
